import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import {
  ChevronDown,
  ChevronRight,
  CalendarCheck,
  CalendarDays,
  Globe,
  Repeat,
  MoonStar,
  CircleDot,
  Coins,
} from 'lucide-react';
import Button from '@/components/ui/Button';
import Input from '@/components/ui/Input';
import Select from '@/components/ui/Select';
import Icon from '@/components/AppIcon';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogBody, DialogFooter } from '@/components/ui/Dialog';
import { Checkbox } from '@/components/ui/Checkbox';
import { Switch } from '@/components/ui/Switch';
import StatusBadge from '@/components/ui/StatusBadge';
import { Tooltip } from '@/components/ui/Tooltip';
import { Skeleton } from '@/components/ui/Skeleton';
import AlertDialog from '@/components/ui/AlertDialog';
import { DialogErrorAlert, useDialogError } from '@/components/ui/DialogError';
import { useToast, ToastRenderer } from '@/hooks/useToast';
import { getApiErrorMessage } from '@/utils/apiErrors';
import { cn } from '@/utils/cn';
import RestrictedHolidays from '../../../../Attendance/RestrictedHolidays';
import { getHolidays, saveHoliday, updateHoliday, deleteHoliday, importHolidaysFile } from '../../../../../api/Attendance/leaves';
import { getBranches } from '../../../../../api/General/Company';
import { getCountries, getUsersCountries } from '../../../../../api/General/Country';
import type { LeavePolicyView, HolidayView, HolidayInput } from '../../../../../models/attendance';
import type { Branch, Country } from '../../../../../models/general';
import { HolidayType, Recurrence } from '../../../../../models/attendance';

interface HolidaysLocationsTabProps {
  currentPolicy?: LeavePolicyView;
}

/**
 * A holiday group has THREE states, not two. `partial` is not a display nicety: while any country
 * row is on, the holiday is in force there — those employees get the day off and, if it is
 * mandatory, still earn the 1.50x public-holiday overtime premium. Anything that flattens `partial`
 * into `off` states the opposite of what is happening to somebody's pay.
 */
type HolidayGroupStatus = 'active' | 'partial' | 'off';

/**
 * One holiday is stored as N rows — one per country — each carrying its own date and its own
 * enabled flag. A flat list therefore renders a three-country tenant three visually identical
 * rows, which is what this grouping exists to stop.
 *
 * The group key is the NAME, deliberately: that is what the server groups on for
 * `applyToAllCountries` / `allCountries`. Grouping the UI on anything narrower (name + year, say)
 * would promise a scope the server will not honour on save.
 */
interface HolidayGroup {
  key: string;
  name: string;
  /** The row a group-level edit/delete is addressed to. */
  anchor: HolidayView;
  rows: HolidayView[];
  dates: string[];
  countryNames: string[];
  branches: string[];
  enabledCount: number;
  /**
   * All on / some on / none on, derived from the enabled counts ALONE. It used to be reported only
   * inside the `mixedDates` branch, so the ordinary case — countries sharing a date, one of them
   * switched off — rendered as a plain "Off" switch.
   */
  status: HolidayGroupStatus;
  /** Countries carry different dates. A group-wide save would flatten them, so it is gated. */
  mixedDates: boolean;
  firstDate?: string;
  lastDate?: string;
  isPast: boolean;
}

type StatusFilter = 'all' | 'active' | 'off';

/** Only the slice of the store this tab reads. */
interface UserSliceState {
  user?: { userId?: string; company?: { id?: number } };
}

const dayKey = (date?: string): string => (date ? date.split('T')[0] : '');

const HolidaysLocationsTab: React.FC<HolidaysLocationsTabProps> = ({ currentPolicy }) => {
  const { t, i18n } = useTranslation('leaves');
  const companyId = useSelector((state: UserSliceState) => state.user?.company?.id);
  const userId = useSelector((state: UserSliceState) => state.user?.userId);
  // CO-575 w2: this tab had NO error surface at all for its two writes. `useToast`/`ToastRenderer`
  // is the surface the sibling tabs already use (EntitlementsAccrualsTab), so the copy, the
  // placement and the `data-testid="app-toast"` hook stay consistent across the settings page.
  const { toast, showToast, dismissToast } = useToast();
  // A refused save must say so INSIDE the dialog — the toast is peripheral, and the previous
  // version of `handleSaveHoliday` swallowed the rejection entirely.
  const dialogError = useDialogError();

  const [holidays, setHolidays] = useState<HolidayView[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [showModal, setShowModal] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [restrictedExpanded, setRestrictedExpanded] = useState(false);
  const [apiBranches, setApiBranches] = useState<Branch[]>([]);
  const [allCountries, setAllCountries] = useState<Country[]>([]);
  // ISO2 codes, not ids: `HolidayInput.countries` is resolved server-side against
  // Name/Nicename/Iso/Iso3, so the wire carries a code and never our internal country id.
  const [inUseCountryIsos, setInUseCountryIsos] = useState<string[]>([]);
  const [expandedOverride, setExpandedOverride] = useState<Record<string, boolean>>({});
  const [typeFilter, setTypeFilter] = useState<HolidayType | ''>('');
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  const [togglingId, setTogglingId] = useState<number | null>(null);

  /** `null` = adding. `applyToAll` maps 1:1 onto the server's `applyToAllCountries`. */
  const [editing, setEditing] = useState<{ row: HolidayView; group: HolidayGroup; applyToAll: boolean } | null>(null);
  const [pendingDelete, setPendingDelete] = useState<{ group: HolidayGroup; row: HolidayView | null } | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const emptyForm = useCallback((): HolidayInput => ({
    name: '',
    description: '',
    date: '',
    holidayType: HolidayType.Public,
    locations: '',
    recurrence: Recurrence.Annually,
    isMandatory: true,
    enabled: true,
    countries: [],
  }), []);

  const [newHoliday, setNewHoliday] = useState<HolidayInput>(emptyForm);

  /* ── loads ─────────────────────────────────────────────────────────────── */

  const loadHolidays = useCallback(async () => {
    setIsLoading(true);
    setLoadError(null);
    try {
      const result = await getHolidays();
      if (result?.errors?.length) {
        const msg = result.errors[0]?.description || result.errors[0]?.message;
        setLoadError(msg || t('translation:somethingWentWrong', 'Failed to load holidays'));
      } else {
        // Normalised so this tab renders correctly against BOTH shapes of the API: before the
        // server ships `enabled`/`countryId` the field is absent, and every row it returned was an
        // enabled one (it filtered archived rows out), so `undefined` must read as enabled.
        setHolidays((result.payload ?? []).map(h => ({ ...h, enabled: h.enabled !== false })));
      }
    } catch {
      setLoadError(t('translation:somethingWentWrong', 'Failed to load holidays'));
    } finally {
      setIsLoading(false);
    }
  }, [t]);

  useEffect(() => {
    loadHolidays();
  }, [loadHolidays]);

  useEffect(() => {
    if (companyId) {
      getBranches(companyId).then(result => {
        if (!result?.errors?.length) setApiBranches(result.payload ?? []);
      }).catch(() => {});
    }
  }, [companyId]);

  // Two reads, each failing on its own. `getCountries` is the full ISO list — HR must be able to
  // configure a calendar for a country before anyone is hired there. `getUsersCountries` is the
  // exact set the server fans out to by default, so it is what gets pre-selected and marked.
  useEffect(() => {
    getCountries().then(result => {
      if (!result?.errors?.length) setAllCountries(result.payload ?? []);
    }).catch(() => {});
  }, []);

  useEffect(() => {
    if (!userId) return;
    getUsersCountries(userId).then(result => {
      if (!result?.errors?.length) {
        setInUseCountryIsos((result.payload ?? []).map(c => c.iso).filter(Boolean));
      }
    }).catch(() => {});
  }, [userId]);

  /* ── options ───────────────────────────────────────────────────────────── */

  const holidayTypeOptions = useMemo(() => [
    { value: HolidayType.Public, label: t('Public Holiday') },
    { value: HolidayType.National, label: t('National Holiday') },
    { value: HolidayType.Religious, label: t('Religious Holiday') },
    { value: HolidayType.Company, label: t('Company Holiday') }
  ], [t]);

  const recurringOptions = useMemo(() => [
    { value: Recurrence.Annually, label: t('Annually (Gregorian)') },
    { value: Recurrence.Lunar, label: t('Lunar Calendar') },
    { value: Recurrence.OneTime, label: t('One-time only') }
  ], [t]);

  const locationOptions = useMemo(
    () => apiBranches.map(b => ({ value: b.name, label: b.name })),
    [apiBranches]
  );

  /**
   * Countries the company already employs people in come first and say so; the rest of the world
   * follows alphabetically. Same control, one list, no second "advanced" picker to discover.
   */
  const countryOptions = useMemo(() => {
    const inUse = new Set(inUseCountryIsos);
    const byName = (a: Country, b: Country) => (a.name ?? '').localeCompare(b.name ?? '');
    const usable = allCountries.filter(c => !!c.iso);
    const used = usable.filter(c => inUse.has(c.iso)).sort(byName);
    const rest = usable.filter(c => !inUse.has(c.iso)).sort(byName);
    return [...used, ...rest].map(c => ({
      value: c.iso,
      label: c.name,
      description: inUse.has(c.iso) ? t('holidayCountryHasEmployees') : t('holidayCountryNoEmployees'),
    }));
  }, [allCountries, inUseCountryIsos, t]);

  const countryById = useMemo(() => {
    const map = new Map<number, Country>();
    allCountries.forEach(c => map.set(c.id, c));
    return map;
  }, [allCountries]);

  /* ── formatting ────────────────────────────────────────────────────────── */

  const formatDate = useCallback((date?: string): string => {
    if (!date) return '—';
    const parsed = new Date(date);
    if (Number.isNaN(parsed.getTime())) return '—';
    return parsed.toLocaleDateString(i18n.language, { year: 'numeric', month: 'short', day: 'numeric' });
  }, [i18n.language]);

  const parseLocations = (locations?: string): string[] =>
    (locations ?? '').split(',').map(l => l.trim()).filter(Boolean);

  const countryLabel = useCallback(
    (row: HolidayView): string =>
      row.countryName || countryById.get(row.countryId)?.name || t('holidayUnknownCountry'),
    [countryById, t]
  );

  /** The token `HolidayInput.countries` expects for a row: its ISO2 code. */
  const countryToken = useCallback(
    (row: HolidayView): string | undefined =>
      countryById.get(row.countryId)?.iso || row.countryName || undefined,
    [countryById]
  );

  /* ── grouping ──────────────────────────────────────────────────────────── */

  const todayKey = useMemo(() => {
    const now = new Date();
    const pad = (n: number) => String(n).padStart(2, '0');
    return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`;
  }, []);

  const groups = useMemo<HolidayGroup[]>(() => {
    const buckets = new Map<string, HolidayView[]>();
    for (const h of holidays) {
      const key = (h.name ?? '').trim().toLowerCase();
      const bucket = buckets.get(key);
      if (bucket) bucket.push(h); else buckets.set(key, [h]);
    }

    const built = Array.from(buckets.entries()).map(([key, rows]) => {
      const sorted = [...rows].sort((a, b) => {
        const byDate = dayKey(a.date).localeCompare(dayKey(b.date));
        return byDate !== 0 ? byDate : countryLabel(a).localeCompare(countryLabel(b));
      });
      const anchor = sorted.find(r => r.enabled) ?? sorted[0];
      const dates = Array.from(new Set(sorted.map(r => dayKey(r.date)).filter(Boolean))).sort();
      const enabledCount = sorted.filter(r => r.enabled).length;
      const lastDate = dates[dates.length - 1];
      return {
        key,
        name: anchor.name,
        anchor,
        rows: sorted,
        dates,
        countryNames: Array.from(new Set(sorted.map(countryLabel))),
        branches: Array.from(new Set(sorted.flatMap(r => parseLocations(r.locations)))),
        enabledCount,
        status: enabledCount === 0 ? 'off' : enabledCount === sorted.length ? 'active' : 'partial',
        mixedDates: dates.length > 1,
        firstDate: dates[0],
        lastDate,
        isPast: !!lastDate && lastDate < todayKey,
      } as HolidayGroup;
    });

    // What HR is configuring sits at the top: upcoming holidays in calendar order, then history
    // most-recent-first underneath.
    return built.sort((a, b) => {
      if (a.isPast !== b.isPast) return a.isPast ? 1 : -1;
      const av = a.firstDate ?? '';
      const bv = b.firstDate ?? '';
      if (av === bv) return a.name.localeCompare(b.name);
      return a.isPast ? bv.localeCompare(av) : av.localeCompare(bv);
    });
  }, [holidays, countryLabel, todayKey]);

  /**
   * Both status filters mean "in at least one country", which is the SAME definition the toolbar
   * count above uses. The `off` test used to be `enabledCount > 0` — i.e. only a holiday switched
   * off EVERYWHERE qualified — so a holiday switched off in one of two countries was counted by the
   * chip and then hidden by the very filter the chip sends you to ("2 switched off" over "No
   * holidays match these filters"). A partial holiday is honestly both things at once: active
   * somewhere and off somewhere. It therefore appears under either filter, and its own badge in the
   * list says which countries.
   */
  const visibleGroups = useMemo(() => groups.filter(g => {
    if (typeFilter && g.anchor.holidayType !== typeFilter) return false;
    if (statusFilter === 'active' && g.enabledCount === 0) return false;
    if (statusFilter === 'off' && g.enabledCount === g.rows.length) return false;
    return true;
  }), [groups, typeFilter, statusFilter]);

  const summary = useMemo(() => ({
    holidayCount: groups.length,
    countryCount: new Set(holidays.map(h => h.countryId)).size,
    // Counted as HOLIDAYS, not rows, so the number and the filter below it describe one set. The
    // row count that stood here disagreed with the filter by construction: it counted the two
    // country rows of a partially-off holiday while the filter refused to show that holiday at all.
    offCount: groups.filter(g => g.enabledCount < g.rows.length).length,
  }), [groups, holidays]);

  const isExpanded = (group: HolidayGroup): boolean =>
    expandedOverride[group.key] ?? (group.status === 'partial' || group.mixedDates);

  const toggleExpanded = (group: HolidayGroup): void =>
    setExpandedOverride(prev => ({ ...prev, [group.key]: !isExpanded(group) }));

  /* ── writes ────────────────────────────────────────────────────────────── */

  const inputFromRow = (row: HolidayView, overrides: Partial<HolidayInput> = {}): HolidayInput => ({
    name: row.name,
    description: row.description || undefined,
    date: dayKey(row.date) || undefined,
    holidayType: row.holidayType,
    locations: row.locations || undefined,
    recurrence: row.recurrence,
    isMandatory: row.isMandatory,
    enabled: row.enabled,
    ...overrides,
  });

  const handleSaveHoliday = async (): Promise<void> => {
    dialogError.clear();
    const messages: string[] = [];
    if (!newHoliday.name?.trim()) messages.push(t('holidayNameRequired'));
    if (!newHoliday.date) messages.push(t('holidayDateRequired'));
    const editingOneCountry = !!editing && !editing.applyToAll;
    if (!editingOneCountry && !(newHoliday.countries ?? []).length) messages.push(t('holidayCountryRequired'));
    if (messages.length) {
      dialogError.showMessages(messages);
      return;
    }

    setIsSaving(true);
    try {
      const input: HolidayInput = {
        name: newHoliday.name.trim(),
        description: newHoliday.description || undefined,
        date: newHoliday.date || undefined,
        holidayType: newHoliday.holidayType,
        locations: newHoliday.locations || undefined,
        recurrence: newHoliday.recurrence,
        isMandatory: newHoliday.isMandatory,
        enabled: newHoliday.enabled,
        // A single-country edit must not carry a country set: the server only reads `countries`
        // when applyToAllCountries is true, and it would fan the holiday back out.
        ...(editingOneCountry ? {} : { countries: newHoliday.countries }),
      };
      const result = editing
        ? await updateHoliday(editing.row.id, input, editing.applyToAll)
        : await saveHoliday(input);
      if (result?.errors?.length) {
        dialogError.capture(result, t('holidaySaveError'));
        return;
      }
      await loadHolidays();
      resetForm();
    } catch (err) {
      dialogError.capture(err, t('holidaySaveError'));
    } finally {
      setIsSaving(false);
    }
  };

  const openAdd = (): void => {
    dialogError.clear();
    setEditing(null);
    // The server's own default, made visible instead of implicit — and narrowable from here.
    setNewHoliday({ ...emptyForm(), countries: [...inUseCountryIsos] });
    setShowModal(true);
  };

  const openEdit = (group: HolidayGroup, row: HolidayView | null): void => {
    dialogError.clear();
    const target = row ?? group.anchor;
    setEditing({ row: target, group, applyToAll: !row });
    setNewHoliday({
      name: target.name,
      description: target.description ?? '',
      date: dayKey(target.date),
      holidayType: target.holidayType,
      locations: target.locations ?? '',
      recurrence: target.recurrence,
      isMandatory: target.isMandatory,
      enabled: target.enabled,
      countries: row
        ? []
        : group.rows.map(countryToken).filter((iso): iso is string => !!iso),
    });
    setShowModal(true);
  };

  const handleToggleEnabled = async (row: HolidayView, enabled: boolean, applyToAll: boolean): Promise<void> => {
    setTogglingId(row.id);
    try {
      const result = await updateHoliday(row.id, inputFromRow(row, { enabled }), applyToAll);
      if (result?.errors?.length) {
        showToast(getApiErrorMessage(result, t, t('holidayToggleError')), 'error');
        return;
      }
      await loadHolidays();
    } catch (err) {
      showToast(getApiErrorMessage(err, t, t('holidayToggleError')), 'error');
    } finally {
      setTogglingId(null);
    }
  };

  const confirmDelete = async (): Promise<void> => {
    if (!pendingDelete) return;
    const { group, row } = pendingDelete;
    setIsDeleting(true);
    try {
      const result = await deleteHoliday((row ?? group.anchor).id, !row);
      if (result?.errors?.length) {
        showToast(getApiErrorMessage(result, t, t('holidayDeleteError')), 'error');
        return;
      }
      await loadHolidays();
      setPendingDelete(null);
    } catch (err) {
      // CO-575 w2: the comment that stood here — "handled by API layer" — was a FALSE PREMISE.
      // `utils/requestHandling.ts` toasts exactly ONE thing globally (the tier-locked 403) and
      // REJECTS every other non-2xx with the parsed body, so the `result.errors` branch above is
      // unreachable on failure and the refusal landed here, where it reached nobody: the holiday
      // simply stayed in the list with no explanation and the user pressed Delete again.
      showToast(getApiErrorMessage(err, t, t('holidayDeleteError')), 'error');
    } finally {
      setIsDeleting(false);
    }
  };

  const resetForm = (): void => {
    setShowModal(false);
    setEditing(null);
    dialogError.clear();
    setNewHoliday(emptyForm());
  };

  const handleImportHolidays = (): void => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = '.csv,.xlsx,.ics';
    input.onchange = async (e: Event) => {
      const file = (e.target as HTMLInputElement)?.files?.[0];
      if (file) {
        try {
          const result = await importHolidaysFile(file);
          if (!result?.errors?.length) {
            await loadHolidays();
          } else {
            showToast(getApiErrorMessage(result, t, t('holidayImportError')), 'error');
          }
        } catch (err) {
          // CO-575 w2: same FALSE PREMISE as the delete path. A rejected import (wrong columns, a
          // locked tier, a permission denial) left the user staring at an unchanged holiday list
          // with no way to tell the upload from a silent no-op. The server explains exactly what it
          // refused — say it.
          showToast(getApiErrorMessage(err, t, t('holidayImportError')), 'error');
        }
      }
    };
    input.click();
  };

  const getLocationsForSelect = (): string[] => parseLocations(newHoliday.locations);

  const setLocationsFromSelect = (values: string[]): void =>
    setNewHoliday(prev => ({ ...prev, locations: values.join(', ') }));

  /* ── presentation helpers ──────────────────────────────────────────────── */

  const typeBadgeVariant = (type: HolidayType): 'info' | 'success' | 'indigo' | 'orange' | 'neutral' => {
    switch (type) {
      case HolidayType.Public: return 'info';
      case HolidayType.National: return 'success';
      case HolidayType.Religious: return 'indigo';
      case HolidayType.Company: return 'orange';
      default: return 'neutral';
    }
  };

  const typeLabel = (type: HolidayType): string =>
    holidayTypeOptions.find(o => o.value === type)?.label ?? String(type);

  /**
   * The badge used to say "Recurring" for both Annually and Lunar, which is the opposite of the
   * truth for Lunar: a Hijri date does not carry forward, so somebody has to type next year's
   * Gregorian date. The badge now says which of the three it is, and a lunar holiday whose latest
   * date has already passed says out loud that next year is missing.
   */
  const recurrenceBadge = (group: HolidayGroup): React.ReactNode => {
    if (group.anchor.recurrence === Recurrence.Lunar) {
      return (
        <div className="flex flex-wrap items-center gap-2">
          <Tooltip content={t('holidayLunarHint')}>
            <StatusBadge variant="warning" size="sm" icon={MoonStar}>{t('holidayLunarNoRepeat')}</StatusBadge>
          </Tooltip>
          {group.isPast && (
            <span className="text-xs font-medium text-amber-700 dark:text-amber-300">
              {t('holidayLunarNeedsDate')}
            </span>
          )}
        </div>
      );
    }
    if (group.anchor.recurrence === Recurrence.OneTime) {
      return (
        <Tooltip content={t('holidayOneTimeHint')}>
          <StatusBadge variant="neutral" size="sm" icon={CircleDot}>{t('holidayOneTimeOnly')}</StatusBadge>
        </Tooltip>
      );
    }
    return (
      <Tooltip content={t('holidayRepeatsEveryYearHint')}>
        <StatusBadge variant="info" size="sm" icon={Repeat}>{t('holidayRepeatsEveryYear')}</StatusBadge>
      </Tooltip>
    );
  };

  const recurrenceCaption = (recurrence: Recurrence): string => {
    if (recurrence === Recurrence.Lunar) return t('holidayLunarHint');
    if (recurrence === Recurrence.OneTime) return t('holidayOneTimeHint');
    return t('holidayRepeatsEveryYearHint');
  };

  const dateSummary = (group: HolidayGroup): string => {
    if (group.dates.length === 0) return t('holidayNoDate');
    if (group.dates.length === 1) return formatDate(group.dates[0]);
    return t('holidayDateRange', { from: formatDate(group.firstDate), to: formatDate(group.lastDate) });
  };

  /**
   * A past date is accepted on purpose — back-filling the current year's calendar is the commonest
   * holiday task, and the server's old "cannot be in the past" reject was deleted for exactly that
   * reason. What was missing is that the stored date ALWAYS counts: saving one retroactively marks a
   * day that has already happened, rewriting what attendance and the Attendance & Overtime report
   * say about it. The AI path has said so since the reject went (`HolidayToolParams.PastDateWarning`
   * in LeaveConfigTools.cs); this form said nothing at all. Non-blocking by design: it explains the
   * consequence, it does not gate the save.
   *
   * Scoped to a CHANGE, not merely to a past value: reopening a holiday that already sits in the
   * past to fix its spelling asserts nothing new, and a warning that fires on every such edit is one
   * people learn to click past. It fires on an add, and on an edit that moves the date into (or
   * around) the past — the cases where saving actually rewrites a day that has already happened.
   *
   * `group.isPast` next to this is a different question — it is about the LATEST stored date of an
   * existing group, and drives sort order and the lunar "next year is missing" note.
   */
  const formDateIsPast = !!newHoliday.date
    && dayKey(newHoliday.date) < todayKey
    && dayKey(newHoliday.date) !== dayKey(editing?.row.date);

  const countrySummary = (group: HolidayGroup): React.ReactNode => {
    const shown = group.countryNames.slice(0, 3).join(', ');
    const extra = group.countryNames.length - 3;
    const text = extra > 0 ? `${shown} ${t('holidayCountriesMore', { n: extra })}` : shown;
    if (extra <= 0) return text;
    return <Tooltip content={group.countryNames.join(', ')}><span>{text}</span></Tooltip>;
  };

  /* ── render ────────────────────────────────────────────────────────────── */

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
        <div className="min-w-0">
          <h3 className="text-lg font-semibold text-foreground">{t('Holidays & Locations')}</h3>
          <p className="max-w-[70ch] text-sm text-muted-foreground">{t('holidaysTabSubtitle')}</p>
        </div>
        <div className="flex shrink-0 gap-2">
          <Button variant="outline" iconName="Upload" onClick={handleImportHolidays} data-testid="import-holidays-btn">
            {t('Import')}
          </Button>
          <Button variant="default" iconName="Plus" onClick={openAdd} data-testid="add-holiday-btn">
            {t('Add Holiday')}
          </Button>
        </div>
      </div>

      {currentPolicy && (
        <div className="rounded-lg border border-primary/20 bg-primary/10 p-4">
          <div className="flex items-center gap-2">
            <Icon name="Shield" size={16} className="text-primary" />
            <span className="text-sm font-semibold text-foreground">{t('Current Policy')}: {currentPolicy?.name}</span>
          </div>
        </div>
      )}

      {/* Summary + filters. Replaces four count tiles that, once a holiday became N country rows,
          counted rows rather than holidays and silently trebled on a three-country tenant. */}
      {!isLoading && !loadError && holidays.length > 0 && (
        <div className="flex flex-col gap-3 rounded-lg border border-border bg-muted/30 px-4 py-3 md:flex-row md:items-center md:justify-between">
          <div className="flex flex-wrap items-center gap-x-4 gap-y-2 text-sm text-muted-foreground">
            <span>
              <span className="font-semibold tabular-nums text-foreground">{summary.holidayCount}</span>{' '}
              {t('holidayCountLabel')}
            </span>
            <span>
              <span className="font-semibold tabular-nums text-foreground">{summary.countryCount}</span>{' '}
              {t('holidayCountriesLabel')}
            </span>
            {summary.offCount > 0 && (
              // The tooltip carries the exact definition so the chip itself can stay scannable —
              // and so nobody has to reverse-engineer it from a count that disagrees with a filter.
              <Tooltip content={t('holidayOffCountHelp')}>
                <StatusBadge variant="warning" size="sm" withDot data-testid="holidays-off-count">
                  {t('holidayOffCount', { n: summary.offCount })}
                </StatusBadge>
              </Tooltip>
            )}
          </div>
          <div className="flex flex-wrap items-center gap-2">
            {/* Select forwards `data-testid` onto its own `role="combobox"` button, so a
                `[data-testid=x] [role=combobox]` selector matches nothing. These wrappers make the
                field addressable both ways without touching the shared component. */}
            <div className="w-full sm:w-48" data-testid="holidays-type-filter-field">
              <Select
                value={typeFilter}
                onChange={(value: string) => setTypeFilter(value as HolidayType | '')}
                options={[{ value: '', label: t('holidayFilterAllTypes') }, ...holidayTypeOptions]}
                data-testid="holidays-type-filter"
              />
            </div>
            <div className="w-full sm:w-48" data-testid="holidays-status-filter-field">
              <Select
                value={statusFilter}
                onChange={(value: string) => setStatusFilter(value as StatusFilter)}
                options={[
                  { value: 'all', label: t('holidayFilterAllStatuses') },
                  { value: 'active', label: t('holidayFilterActive') },
                  { value: 'off', label: t('holidayFilterOff') },
                ]}
                data-testid="holidays-status-filter"
              />
            </div>
          </div>
        </div>
      )}

      {isLoading && (
        <div className="space-y-4" aria-busy="true" aria-label={String(t('Loading holidays...'))}>
          {[0, 1, 2].map(i => (
            <div key={i} className="rounded-lg border border-border bg-card p-5">
              <Skeleton className="h-5 w-48" />
              <Skeleton className="mt-3 h-4 w-72" />
              <Skeleton className="mt-2 h-4 w-40" />
            </div>
          ))}
        </div>
      )}

      {!isLoading && loadError && (
        <div className="rounded-lg border border-destructive/20 bg-destructive/10 py-12 text-center" role="alert" data-testid="holidays-load-error">
          <Icon name="AlertTriangle" size={48} className="mx-auto mb-4 text-destructive/60" />
          <h3 className="mb-2 text-lg font-medium text-destructive">{t('translation:error', 'Error')}</h3>
          <p className="mb-4 text-sm text-destructive/80">{loadError}</p>
          <Button variant="default" iconName="RefreshCw" onClick={loadHolidays} data-testid="holidays-retry">
            {t('translation:retry', 'Retry')}
          </Button>
        </div>
      )}

      {!isLoading && !loadError && holidays.length === 0 && (
        <div className="rounded-lg border border-border bg-muted/50 py-12 text-center">
          <Icon name="Calendar" size={48} className="mx-auto mb-4 text-muted-foreground/60" />
          <h3 className="mb-2 text-lg font-medium text-foreground">{t('No holidays configured')}</h3>
          <p className="mb-4 text-sm text-muted-foreground">{t('Add holidays to automatically exclude them from leave calculations.')}</p>
          <Button variant="default" iconName="Plus" onClick={openAdd} data-testid="leave-holidays-add-empty">
            {t('Add Holiday')}
          </Button>
        </div>
      )}

      {!isLoading && !loadError && holidays.length > 0 && visibleGroups.length === 0 && (
        <div className="rounded-lg border border-border bg-muted/50 py-12 text-center" data-testid="holidays-no-match">
          <h3 className="mb-2 text-base font-medium text-foreground">{t('holidayNoMatchTitle')}</h3>
          <p className="mb-4 text-sm text-muted-foreground">{t('holidayNoMatchBody')}</p>
          <Button variant="outline" onClick={() => { setTypeFilter(''); setStatusFilter('all'); }} data-testid="holidays-clear-filters">
            {t('holidayClearFilters')}
          </Button>
        </div>
      )}

      {/* Grouped list: one card per holiday NAME, its countries as a second layer. */}
      {!isLoading && !loadError && visibleGroups.length > 0 && (
        <div className="space-y-3">
          {visibleGroups.map(group => {
            const expanded = isExpanded(group);
            const multiCountry = group.rows.length > 1;
            /**
             * Two independent reasons to withhold the group-wide switch, and the second one is the
             * defect this replaced.
             *
             * 1. `mixedDates` — a group-wide save sends the anchor's date to every row, so it would
             *    flatten dates the countries deliberately keep apart.
             * 2. `partial` — a two-position control cannot represent three states. Rendering one
             *    anyway forced `checked` to a boolean, and "some countries on" collapsed to
             *    `aria-checked=false` labelled "Off" while those countries were still observing the
             *    holiday and still earning the 1.50x mandatory-holiday premium on it. The header was
             *    asserting the opposite of the truth about somebody's pay.
             *
             * In both cases the per-country switches one layer down are the honest control, and the
             * group is already expanded by default in exactly these two cases (see `isExpanded`).
             */
            const groupSwitchAllowed = group.status !== 'partial' && !group.mixedDates;
            // Keyed on the row id, not the name: a name is free text and may be entirely Arabic,
            // which does not survive being slugged into a DOM id.
            const panelId = `holiday-countries-${group.anchor.id}`;

            return (
              <div key={group.key} className="rounded-lg border border-border bg-card" data-testid="holiday-item">
                <div className="flex flex-col gap-3 p-4 sm:flex-row sm:items-start sm:justify-between sm:p-5">
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-2">
                      {multiCountry && (
                        <button
                          type="button"
                          onClick={() => toggleExpanded(group)}
                          aria-expanded={expanded}
                          aria-controls={panelId}
                          aria-label={String(expanded ? t('holidayCollapseLabel') : t('holidayExpandLabel'))}
                          className="rounded p-0.5 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/20"
                          data-testid="holiday-group-toggle"
                        >
                          <ChevronDown
                            className={cn(
                              'h-4 w-4 transition-transform duration-150',
                              !expanded && '-rotate-90 rtl:rotate-90'
                            )}
                          />
                        </button>
                      )}
                      <h4 className={cn('truncate text-base font-semibold', group.status === 'off' ? 'text-muted-foreground' : 'text-foreground')}>
                        {group.name}
                      </h4>
                      <StatusBadge variant={typeBadgeVariant(group.anchor.holidayType)} size="sm">
                        {typeLabel(group.anchor.holidayType)}
                      </StatusBadge>
                      {group.anchor.isMandatory && (
                        <Tooltip content={t('holidayMandatoryHelp')}>
                          <StatusBadge variant="neutral" size="sm" icon={Coins}>{t('Mandatory')}</StatusBadge>
                        </Tooltip>
                      )}
                    </div>

                    <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1.5 text-sm text-muted-foreground">
                      <span className="inline-flex items-center gap-1.5">
                        <CalendarDays className="h-4 w-4 shrink-0" aria-hidden="true" />
                        <span className="tabular-nums">{dateSummary(group)}</span>
                      </span>
                      <span className="inline-flex min-w-0 items-center gap-1.5">
                        <Globe className="h-4 w-4 shrink-0" aria-hidden="true" />
                        <span className="truncate">{countrySummary(group)}</span>
                      </span>
                      {group.mixedDates && (
                        <StatusBadge variant="info" size="sm">{t('holidayDatesDiffer')}</StatusBadge>
                      )}
                    </div>

                    <div className="mt-2">{recurrenceBadge(group)}</div>

                    {group.anchor.description && (
                      <p className="mt-3 max-w-[70ch] text-sm text-muted-foreground">{group.anchor.description}</p>
                    )}

                    {group.branches.length > 0 && (
                      <div className="mt-3 flex flex-wrap items-center gap-2">
                        <span className="text-xs font-medium text-muted-foreground">{t('Applicable Branches')}</span>
                        {group.branches.map(loc => (
                          <span key={loc} className="rounded bg-muted px-2 py-0.5 text-xs text-muted-foreground">{loc}</span>
                        ))}
                      </div>
                    )}
                  </div>

                  <div className="flex shrink-0 items-center gap-3 self-start">
                    {groupSwitchAllowed ? (
                      <div className="flex items-center gap-2">
                        <Switch
                          checked={group.status === 'active'}
                          onCheckedChange={(checked) => handleToggleEnabled(group.anchor, checked, true)}
                          disabled={togglingId !== null}
                          aria-label={String(t('holidayToggleGroupLabel', { name: group.name }))}
                          data-testid="holiday-group-enabled"
                        />
                        <span className={cn('text-xs font-medium', group.status === 'active' ? 'text-foreground' : 'text-muted-foreground')}>
                          {group.status === 'active' ? t('holidayStatusActive') : t('holidayStatusOff')}
                        </span>
                      </div>
                    ) : group.status === 'partial' ? (
                      // The count alone ("1 of 2 active") is accurate but not yet actionable — the
                      // consequence HR needs is that the holiday is still being observed and still
                      // priced. That sentence lives in the tooltip; the badge stays scannable.
                      <Tooltip content={t('holidayStatusPartialHelp')}>
                        <StatusBadge variant="warning" size="sm" withDot data-testid="holiday-group-status">
                          {t('holidayStatusPartial', { on: group.enabledCount, total: group.rows.length })}
                        </StatusBadge>
                      </Tooltip>
                    ) : (
                      <StatusBadge
                        variant={group.status === 'active' ? 'success' : 'neutral'}
                        size="sm"
                        withDot
                        data-testid="holiday-group-status"
                      >
                        {group.status === 'active' ? t('holidayStatusActive') : t('holidayStatusOff')}
                      </StatusBadge>
                    )}
                    <Button
                      variant="ghost"
                      size="sm"
                      iconName="Edit"
                      onClick={() => openEdit(group, null)}
                      title={String(multiCountry ? t('holidayEditAllCountries') : t('Edit Holiday'))}
                      aria-label={String(multiCountry ? t('holidayEditAllCountries') : t('Edit Holiday'))}
                      data-testid="leave-holidays-edit"
                    />
                    <Button
                      variant="ghost"
                      size="sm"
                      iconName="Trash2"
                      onClick={() => setPendingDelete({ group, row: null })}
                      className="text-destructive hover:text-destructive/80"
                      title={String(multiCountry ? t('holidayDeleteEverywhere') : t('holidayDeleteCta'))}
                      aria-label={String(multiCountry ? t('holidayDeleteEverywhere') : t('holidayDeleteCta'))}
                      data-testid="leave-holidays-delete"
                    />
                  </div>
                </div>

                {/* The header has no switch in these two cases, so a COLLAPSED group would otherwise
                    show a status with no visible way to change it. Status wins when it applies: it
                    is the newer surprise, and differing dates already have their own badge above. */}
                {multiCountry && !groupSwitchAllowed && !expanded && (
                  <p className="px-4 pb-4 text-xs text-muted-foreground sm:px-5">
                    {group.status === 'partial' ? t('holidayPerCountryStatusHint') : t('holidayPerCountryDatesHint')}
                  </p>
                )}

                {multiCountry && expanded && (
                  <ul id={panelId} className="divide-y divide-border border-t border-border">
                    {group.rows.map(row => (
                      <li
                        key={row.id}
                        className={cn('flex flex-wrap items-center gap-3 px-4 py-3 sm:px-5', !row.enabled && 'bg-muted/40')}
                        data-testid="holiday-country-row"
                      >
                        <Switch
                          checked={row.enabled}
                          onCheckedChange={(checked) => handleToggleEnabled(row, checked, false)}
                          disabled={togglingId !== null}
                          aria-label={String(t('holidayToggleCountryLabel', { name: group.name, country: countryLabel(row) }))}
                          data-testid="holiday-country-enabled"
                        />
                        <span className={cn('min-w-0 flex-1 truncate text-sm font-medium', row.enabled ? 'text-foreground' : 'text-muted-foreground')}>
                          {countryLabel(row)}
                        </span>
                        <span className="text-sm tabular-nums text-muted-foreground">{formatDate(row.date)}</span>
                        <Button
                          variant="ghost"
                          size="sm"
                          iconName="Edit"
                          onClick={() => openEdit(group, row)}
                          title={String(t('holidayEditCountryRow'))}
                          aria-label={String(t('holidayEditCountryRow'))}
                          data-testid="holiday-country-edit"
                        />
                        <Button
                          variant="ghost"
                          size="sm"
                          iconName="Trash2"
                          onClick={() => setPendingDelete({ group, row })}
                          className="text-destructive hover:text-destructive/80"
                          title={String(t('holidayDeleteCountryRow'))}
                          aria-label={String(t('holidayDeleteCountryRow'))}
                          data-testid="holiday-country-delete"
                        />
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Restricted / Optional Holidays — Collapsible Section */}
      <div className="overflow-hidden rounded-lg border border-border" data-testid="restricted-holidays-section">
        <button
          onClick={() => setRestrictedExpanded(prev => !prev)}
          className="flex w-full items-center justify-between bg-muted/50 px-5 py-4 text-start transition-colors hover:bg-muted"
          data-testid="restricted-holidays-toggle"
        >
          <div className="flex items-center gap-3">
            <CalendarCheck className="h-5 w-5 text-indigo-600" />
            <div>
              <h3 className="text-base font-semibold text-foreground">{t('restrictedHolidaysSectionTitle')}</h3>
              <p className="text-sm text-muted-foreground">{t('restrictedHolidaysSectionDescription')}</p>
            </div>
          </div>
          {restrictedExpanded
            ? <ChevronDown className="h-5 w-5 shrink-0 text-muted-foreground" />
            : <ChevronRight className="h-5 w-5 shrink-0 text-muted-foreground rtl:rotate-180" />
          }
        </button>
        {restrictedExpanded && (
          <div className="border-t border-border p-5">
            <RestrictedHolidays embedded />
          </div>
        )}
      </div>

      {/* Add/Edit dialog */}
      <Dialog open={showModal} onOpenChange={(open) => { if (!open && !isSaving) resetForm(); }}>
        <DialogContent
          size="lg"
          onEscapeKeyDown={(e) => { if (isSaving) e.preventDefault(); }}
          onPointerDownOutside={(e) => { if (isSaving) e.preventDefault(); }}
        >
          <DialogHeader>
            <DialogTitle>{editing ? t('holidayEditGroupTitle') : t('Add New Holiday')}</DialogTitle>
          </DialogHeader>
          <DialogBody className="space-y-6">
            <DialogErrorAlert error={dialogError.error} />

            {editing && (
              <div className="rounded-lg border border-border bg-muted/40 p-3 text-sm text-muted-foreground" data-testid="holiday-edit-scope">
                <p className="text-foreground">
                  {editing.applyToAll
                    ? t('holidayScopeAllCountries', { n: editing.group.rows.length })
                    : t('holidayScopeOneCountry', { country: countryLabel(editing.row) })}
                </p>
                {editing.applyToAll && editing.group.mixedDates && (
                  <p className="mt-1.5 text-amber-700 dark:text-amber-300">{t('holidayScopeDateWarning')}</p>
                )}
              </div>
            )}

            <section className="space-y-4">
              <h4 className="text-sm font-semibold text-foreground">{t('holidaySectionWhen')}</h4>
              <div>
                <label htmlFor="holiday-name" className="mb-2 block text-sm font-medium text-muted-foreground">{t('Holiday Name')}</label>
                <Input
                  id="holiday-name"
                  type="text"
                  value={newHoliday.name}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => setNewHoliday(prev => ({ ...prev, name: e.target.value }))}
                  placeholder={t('e.g., National Day')}
                  data-testid="leave-holidays-name"
                />
              </div>
              <div>
                <label htmlFor="holiday-date" className="mb-2 block text-sm font-medium text-muted-foreground">{t('Date')}</label>
                <Input
                  id="holiday-date"
                  type="date"
                  value={newHoliday.date ?? ''}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => setNewHoliday(prev => ({ ...prev, date: e.target.value }))}
                  data-testid="leave-holidays-date"
                />
                {/* The live region is mounted unconditionally and only its CONTENT toggles — a
                    region that appears at the same moment as its text is usually announced by
                    nothing. The wrapper carries no spacing of its own, so it collapses when empty. */}
                <div role="status" aria-live="polite">
                  {formDateIsPast && (
                    <p
                      className="mt-1.5 max-w-[70ch] text-xs text-amber-700 dark:text-amber-300"
                      data-testid="holiday-past-date-warning"
                    >
                      {t('holidayPastDateWarning')}
                      {newHoliday.isMandatory ? ` ${t('holidayPastDateWarningPay')}` : ''}
                    </p>
                  )}
                </div>
              </div>
              <div>
                <label htmlFor="holiday-description" className="mb-2 block text-sm font-medium text-muted-foreground">{t('Description')}</label>
                <textarea
                  id="holiday-description"
                  value={newHoliday.description ?? ''}
                  onChange={(e) => setNewHoliday(prev => ({ ...prev, description: e.target.value }))}
                  placeholder={t('Describe the holiday...')}
                  rows={3}
                  className="w-full rounded-md border border-border bg-card px-3 py-2 text-foreground focus:outline-none focus-visible:border-primary focus-visible:ring-2 focus-visible:ring-primary/20"
                  data-testid="leave-holidays-description"
                />
              </div>
            </section>

            {/* Country and branch are two different axes and the form never said so. */}
            <section className="space-y-4 border-t border-border pt-5">
              <div>
                <h4 className="text-sm font-semibold text-foreground">{t('holidaySectionWhere')}</h4>
                <p className="mt-1 max-w-[70ch] text-xs text-muted-foreground">{t('holidayWhereExplainer')}</p>
              </div>

              <div>
                <label className="mb-2 block text-sm font-medium text-muted-foreground">{t('holidayCountriesField')}</label>
                {editing && !editing.applyToAll ? (
                  <div className="rounded-md border border-border bg-muted/40 px-3 py-2">
                    <span className="text-sm font-medium text-foreground">{countryLabel(editing.row)}</span>
                    <p className="mt-1 text-xs text-muted-foreground">{t('holidayScopeCountryFixed')}</p>
                  </div>
                ) : (
                  <>
                    <Select
                      value={newHoliday.countries ?? []}
                      onChange={(value: string[]) => setNewHoliday(prev => ({ ...prev, countries: value }))}
                      options={countryOptions}
                      multiple
                      searchable
                      placeholder={t('holidayCountriesSelectPlaceholder')}
                      data-testid="leave-holidays-countries"
                    />
                    <p className="mt-1.5 max-w-[70ch] text-xs text-muted-foreground">
                      {editing ? t('holidayCountriesEditHelp') : t('holidayCountriesHelp')}
                    </p>
                    {!editing && inUseCountryIsos.length === 0 && (
                      <p className="mt-1.5 text-xs text-amber-700 dark:text-amber-300">{t('holidayCountriesNoneInUse')}</p>
                    )}
                  </>
                )}
              </div>

              <div>
                <label className="mb-2 block text-sm font-medium text-muted-foreground">{t('Applicable Branches')}</label>
                <Select
                  value={getLocationsForSelect()}
                  onChange={(value: string[]) => setLocationsFromSelect(value)}
                  options={locationOptions}
                  multiple
                  placeholder={t('Select branches')}
                  data-testid="leave-holidays-locations"
                />
                <p className="mt-1.5 max-w-[70ch] text-xs text-muted-foreground">{t('holidayBranchesHelp')}</p>
              </div>
            </section>

            <section className="space-y-4 border-t border-border pt-5">
              <h4 className="text-sm font-semibold text-foreground">{t('holidaySectionHow')}</h4>
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div>
                  <label className="mb-2 block text-sm font-medium text-muted-foreground">{t('Holiday Type')}</label>
                  <Select
                    value={newHoliday.holidayType}
                    onChange={(value: string) => setNewHoliday(prev => ({ ...prev, holidayType: value as HolidayType }))}
                    options={holidayTypeOptions}
                    data-testid="leave-holidays-type"
                  />
                </div>
                <div>
                  <label className="mb-2 block text-sm font-medium text-muted-foreground">{t('holidayRecurrenceField')}</label>
                  <Select
                    value={newHoliday.recurrence}
                    onChange={(value: string) => setNewHoliday(prev => ({ ...prev, recurrence: value as Recurrence }))}
                    options={recurringOptions}
                    data-testid="leave-holidays-recurrence"
                  />
                  {/* The choice is explained where it is made, not discovered a year later. */}
                  <p className="mt-1.5 text-xs text-muted-foreground" data-testid="holiday-recurrence-caption">
                    {recurrenceCaption(newHoliday.recurrence)}
                  </p>
                </div>
              </div>

              <label className="flex items-start gap-2">
                <Checkbox
                  checked={newHoliday.isMandatory}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => setNewHoliday(prev => ({ ...prev, isMandatory: e.target.checked }))}
                  data-testid="leave-holidays-mandatory"
                />
                <span>
                  <span className="block text-sm font-medium text-foreground">{t('Mandatory holiday')}</span>
                  <span className="block text-xs text-muted-foreground">{t('holidayMandatoryHelp')}</span>
                </span>
              </label>

              <div className="flex items-start gap-3 rounded-lg border border-border p-3">
                <Switch
                  checked={newHoliday.enabled}
                  onCheckedChange={(checked) => setNewHoliday(prev => ({ ...prev, enabled: checked }))}
                  aria-label={String(t('holidayActiveField'))}
                  data-testid="leave-holidays-enabled"
                />
                <div>
                  <span className="block text-sm font-medium text-foreground">{t('holidayActiveField')}</span>
                  <span className="block text-xs text-muted-foreground">{t('holidayActiveHelp')}</span>
                </div>
              </div>
            </section>
          </DialogBody>
          <DialogFooter>
            <Button variant="outline" onClick={resetForm} disabled={isSaving} data-testid="leave-holidays-modal-cancel">
              {t('Cancel')}
            </Button>
            <Button variant="default" onClick={handleSaveHoliday} loading={isSaving} disabled={isSaving} data-testid="holiday-save-btn">
              {editing ? t('holidaySaveCta') : t('holidayAddCta')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete confirmation. The scope (this country / every country) is the whole point of the
          dialog, and `window.confirm` could not carry it. */}
      <AlertDialog
        open={!!pendingDelete}
        onOpenChange={(open) => { if (!open && !isDeleting) setPendingDelete(null); }}
        variant="destructive"
        isLoading={isDeleting}
        title={
          pendingDelete?.row
            ? t('holidayDeleteOneTitle', { name: pendingDelete.group.name, country: countryLabel(pendingDelete.row) })
            : t('holidayDeleteAllTitle', { name: pendingDelete?.group.name ?? '' })
        }
        description={
          pendingDelete?.row
            ? t('holidayDeleteOneBody', { country: countryLabel(pendingDelete.row) })
            : t('holidayDeleteAllBody', { n: pendingDelete?.group.rows.length ?? 0 })
        }
        confirmLabel={t('holidayDeleteCta')}
        onConfirm={confirmDelete}
      />

      {/* CO-575 w2 — the refusal surface for Delete/Import. */}
      <ToastRenderer toast={toast} onDismiss={dismissToast} />
    </div>
  );
};

export default HolidaysLocationsTab;
